import { BoxV2 as Box, Flex, useHasPermission } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ListingTab } from '../../Campaigns/components';
import ProfileBanner from '../components/ProfileBanner';
import {
  TabAccount,
  TabEmailPreferences,
  TabUserManagement,
  TabFinance,
} from '../components';
import { User } from '../types';
import { Loader } from '../../../../shared_elements';
import {
  loginsManagerController,
  profileController,
  statisticsController,
} from '@/apis/core';
import { CspProfile } from '@/types/user';

enum ProfileTab {
  Account = 'account',
  Emails = 'emails',
  Users = 'users',
  Finance = 'finance',
}

const ProfileTabItems = [
  { key: ProfileTab.Account, label: 'Account' },
  { key: ProfileTab.Emails, label: 'Email Preferences' },
  { key: ProfileTab.Users, label: 'User Management' },
  { key: ProfileTab.Finance, label: 'Finance' },
];

const ProfileManagementV2: FunctionComponent = () => {
  const canViewFinance = useHasPermission('profile.viewFinance') as boolean;

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(ProfileTab.Account);
  const [profile, setProfile] = useState<CspProfile | undefined>();
  const [brandCount, setBrandCount] = useState<number>(0);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [secondaryRoles, setSecondaryRoles] = useState<string[]>([]);

  const fetchInitialData = async () => {
    const [
      profileResult,
      usersResult,
      rolesResult,
      secondaryRolesResult,
      brandCountResult,
    ] = await Promise.allSettled([
      profileController.getProfile(),
      loginsManagerController.getUsers(),
      loginsManagerController.getRoles('PRIMARY'),
      loginsManagerController.getRoles('SECONDARY'),
      statisticsController.getBrandCount(),
    ]);

    if (profileResult.status === 'fulfilled') {
      setProfile(profileResult.value);
    }
    if (usersResult.status === 'fulfilled') {
      setUsers(usersResult.value);
    }
    if (rolesResult.status === 'fulfilled') {
      setRoles(rolesResult.value);
    }
    if (secondaryRolesResult.status === 'fulfilled') {
      setSecondaryRoles(secondaryRolesResult.value);
    }
    if (brandCountResult.status === 'fulfilled') {
      setBrandCount(brandCountResult.value);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Dynamically filter tabs based on permissions
  const visibleTabs = useMemo(() => {
    return ProfileTabItems.filter(
      (tab) => tab.key !== ProfileTab.Finance || canViewFinance
    );
  }, [canViewFinance]);

  return loading || !profile ? (
    <Loader />
  ) : (
    <Flex
      sx={{ flexDirection: 'column', rowGap: '32px' }}
      data-testid="profileManagementContainer"
    >
      <ProfileBanner
        companyName={profile.companyName}
        displayName={profile.displayName}
        uid={profile.uid}
        entityType={profile.entityType}
      />

      <ListingTab
        items={visibleTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        data-testid="profileTabs"
      />

      <Box>
        {activeTab === ProfileTab.Account && (
          <TabAccount
            profile={profile}
            currentPortalUsers={users?.length}
            currentBrands={brandCount}
          />
        )}
        {activeTab === ProfileTab.Emails && (
          <TabEmailPreferences profile={profile} onUpdate={setProfile} />
        )}

        {activeTab === ProfileTab.Users && (
          <TabUserManagement
            profile={profile}
            users={users}
            onUsersUpdate={setUsers}
            roles={roles}
            secondaryRoles={secondaryRoles}
          />
        )}
        {activeTab === ProfileTab.Finance && <TabFinance />}
      </Box>
    </Flex>
  );
};

export default ProfileManagementV2;
